/**
 * Utility functions for medical scribe streaming
 */
import type { TranscriptEntry } from "../medical-scribe.slice";
import type { NormalizedTranscriptChunk } from "./types";

/**
 * Wait for specified milliseconds
 */
export const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Resolve when promise completes or timeout elapses (returns undefined on timeout).
 *
 * Note: timing out does **not** cancel the underlying promise — it keeps running in
 * the background. Callers must tolerate overlapping worker/IDB operations (the audio
 * worker serializes internally today).
 */
export const withTimeout = async <T>(
  promise: Promise<T>,
  ms: number
): Promise<T | undefined> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<undefined>((resolve) => {
        timeoutId = setTimeout(() => resolve(undefined), ms);
      })
    ]);
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
};

/**
 * Map transcript chunks from backend to transcript entries
 */
export const mapChunksToTranscriptEntries = (
  chunks: NormalizedTranscriptChunk[]
): TranscriptEntry[] => {
  if (!Array.isArray(chunks) || !chunks.length) {
    return [];
  }

  const entries: TranscriptEntry[] = [];

  for (const chunk of chunks) {
    const channel = chunk?.channel;
    const alternatives = channel?.alternatives;
    if (!alternatives || alternatives.length === 0) {
      continue;
    }

    const alternative = alternatives[0];
    const transcriptPart = alternative?.transcript;
    if (!transcriptPart || !transcriptPart.trim()) {
      continue;
    }

    let speaker = alternative?.speaker;
    if (
      (speaker === undefined || speaker === null) &&
      Array.isArray(alternative?.words) &&
      alternative.words.length > 0
    ) {
      speaker = alternative.words[0].speaker;
    }

    entries.push({
      speaker: typeof speaker === "number" ? speaker : 0,
      text: transcriptPart.trim(),
      isFinal: Boolean(chunk?.is_final ?? true),
      timestamp: Date.now()
    });
  }

  return entries;
};

/**
 * Calculate recording duration in HH:MM:SS format, excluding paused time
 * @param startTime - The timestamp when recording started
 * @param totalPausedDuration - Total accumulated paused time in milliseconds
 * @param pauseStartTime - If currently paused, the timestamp when pause started
 */
export const calculateDuration = (
  startTime: number | null,
  totalPausedDuration: number = 0,
  pauseStartTime: number | null = null
): string => {
  if (!startTime) return "00:00";

  // Calculate total elapsed time
  const totalElapsed = Date.now() - startTime;

  // Calculate current pause duration if currently paused
  const currentPauseDuration = pauseStartTime ? Date.now() - pauseStartTime : 0;

  // Subtract all paused time from total elapsed time
  const activeElapsed = Math.floor(
    (totalElapsed - totalPausedDuration - currentPauseDuration) / 1000
  );

  // Ensure we don't have negative duration
  const elapsed = Math.max(0, activeElapsed);

  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};
